import { useEffect, useState } from "react";
import { OverviewAnalytics } from "../components/OverviewAnalytics.tsx";
import {
	type Check,
	type OverviewStatsAnswer,
	type Smoke,
	UnauthorizedError,
	api,
} from "../lib/api.ts";
import { approvalStatus, syncedLabel } from "../lib/format.ts";
import {
	type InstrumentRead,
	type SignalsRead,
	accountCardId,
	instrumentLines,
	overviewCopy,
	readsInstruments,
	signalLines,
	signalsBanner,
} from "../lib/overview-copy.ts";
import {
	ACCOUNTS_PANEL,
	ANALYTICS_SHOWN,
	SHOW_ANALYTICS,
	SYSTEMS,
} from "../lib/overview-layout-copy.ts";
import { analyticsFor } from "../lib/overview-layout.ts";
import { type NamesRead, overviewStatsState } from "../lib/overview-stats.ts";
import { accountCurrency } from "../lib/stats.ts";
import { type Load, useLoad } from "../lib/useLoad.ts";
/**
 * The screen an operator lands on after SSO.
 *
 * Its job is to answer one question: what can I act on right now, and is the
 * machinery behind it up? It used to carry a spend rail on every card — the
 * account's limit drawn as a line to a stop. An X ad account reports no limit
 * and no spend, so the rail is gone until those figures have a source; see the
 * note in `lib/format.ts`.
 */
import { Badge, Button, Card, InfoBanner, Page, Panel } from "../ui/index.ts";

export function Overview() {
	const [access] = useLoad(() => api.myAccess());
	const [accounts] = useLoad(() => api.xAccounts());
	const [smoke] = useLoad(() => api.smoke());

	const list = accounts.state === "ready" ? accounts.data.accounts : [];
	// One call, and this screen only renders — sentences and banners come back
	// finished from lib/overview-copy.ts, where a runner can reach them.
	const copy = overviewCopy(
		accounts.state,
		list.length,
		accounts.state === "ready" && accounts.data.truncated,
		accounts.state === "failed" ? accounts.status : null,
		accounts.state === "failed" ? accounts.why : null,
	);

	/*
	 * 🔴 Each account's funding instruments, read live — one GET per visible account,
	 * and only while there are at most `INSTRUMENTS_READ_UP_TO` of them. The rule is
	 * `readsInstruments` in lib/overview-copy.ts, where its number is pinned; this
	 * effect only obeys it. Keyed by X's account id, cleared whenever the account list
	 * is read again, and a late answer after that is dropped rather than written under
	 * a newer list.
	 */
	const [instruments, setInstruments] = useState<Record<string, InstrumentRead>>({});
	useEffect(() => {
		setInstruments({});
		if (accounts.state !== "ready" || !readsInstruments(accounts.data.accounts.length)) return;
		let live = true;
		for (const a of accounts.data.accounts) {
			api.fundingInstruments(a.xAccountId).then(
				(d) => {
					if (live)
						setInstruments((held) => ({
							...held,
							[a.xAccountId]: { state: "ready", instruments: d.fundingInstruments },
						}));
				},
				(e: unknown) => {
					if (!live || e instanceof UnauthorizedError) return;
					setInstruments((held) => ({
						...held,
						[a.xAccountId]: {
							state: "failed",
							why: e instanceof Error ? e.message : String(e),
						},
					}));
				},
			);
		}
		return () => {
			live = false;
		};
	}, [accounts]);

	/*
	 * Each account's signals — X's words as the scheduled cycle kept them, read from the
	 * mirror, so no call to X from this screen. Same bound as the instruments, same
	 * keying, same rule for a late answer.
	 */
	const [signals, setSignals] = useState<Record<string, SignalsRead>>({});
	useEffect(() => {
		setSignals({});
		if (accounts.state !== "ready" || !readsInstruments(accounts.data.accounts.length)) return;
		let live = true;
		for (const a of accounts.data.accounts) {
			api.signals(a.xAccountId).then(
				(d) => {
					if (live)
						setSignals((held) => ({ ...held, [a.xAccountId]: { state: "ready", answer: d } }));
				},
				(e: unknown) => {
					if (!live || e instanceof UnauthorizedError) return;
					setSignals((held) => ({
						...held,
						[a.xAccountId]: { state: "failed", why: e instanceof Error ? e.message : String(e) },
					}));
				},
			);
		}
		return () => {
			live = false;
		};
	}, [accounts]);

	/*
	 * Each account's figures (A8) — from the mirror the hourly cycle writes
	 * (`GET /api/v1/overview-stats`), never from X. Same bound as the instruments and the
	 * signals, same keying, same rule for a late answer.
	 */
	type StatsRead =
		| { state: "loading" }
		| { state: "failed"; why: string }
		| { state: "ready"; answer: OverviewStatsAnswer };
	const [stats, setStats] = useState<Record<string, StatsRead>>({});
	useEffect(() => {
		setStats({});
		if (accounts.state !== "ready" || !readsInstruments(accounts.data.accounts.length)) return;
		let live = true;
		for (const a of accounts.data.accounts) {
			api.overviewStats(a.xAccountId).then(
				(d) => {
					if (live)
						setStats((held) => ({ ...held, [a.xAccountId]: { state: "ready", answer: d } }));
				},
				(e: unknown) => {
					if (!live || e instanceof UnauthorizedError) return;
					setStats((held) => ({
						...held,
						[a.xAccountId]: { state: "failed", why: e instanceof Error ? e.message : String(e) },
					}));
				},
			);
		}
		return () => {
			live = false;
		};
	}, [accounts]);

	/*
	 * Each account's campaign names, for the analytics table only — one GET /campaigns per
	 * account (X's Core Entity reads, not Analytics), the read the Campaigns screen makes.
	 * Same bound, same keying, same rule for a late answer.
	 */
	const [names, setNames] = useState<Record<string, NamesRead>>({});
	useEffect(() => {
		setNames({});
		if (accounts.state !== "ready" || !readsInstruments(accounts.data.accounts.length)) return;
		let live = true;
		for (const a of accounts.data.accounts) {
			api.campaigns(a.xAccountId).then(
				(d) => {
					if (live)
						setNames((held) => ({
							...held,
							[a.xAccountId]: { state: "ready", campaigns: d.campaigns, truncated: d.truncated },
						}));
				},
				(e: unknown) => {
					if (!live || e instanceof UnauthorizedError) return;
					setNames((held) => ({
						...held,
						[a.xAccountId]: { state: "failed", why: e instanceof Error ? e.message : String(e) },
					}));
				},
			);
		}
		return () => {
			live = false;
		};
	}, [accounts]);

	/*
	 * Whose analytics stand beside the cards — one account at a time (the one-screen
	 * layout, PR 5 of audits/2026-10-29/gui/plan.md). Local to this screen: the global
	 * account choice is the integration round's, not this one's.
	 */
	const [chosen, setChosen] = useState<string | null>(null);
	const shown = analyticsFor(list, chosen);

	const signalBanner = signalsBanner(signals, list);
	const banners = signalBanner ? [...copy.banners, signalBanner] : copy.banners;

	return (
		<Page
			fill
			title={copy.headline}
			description={copy.subhead}
			actions={
				access.state === "ready" ? (
					<Badge variant={access.data.inherited ? "info" : "default"}>
						{roleLabel(access.data.role)}
					</Badge>
				) : null
			}
		>
			<div className="grid h-full min-h-0 grid-rows-[auto_auto_minmax(0,1fr)] gap-3">
				<section className="flex flex-wrap items-stretch gap-px bg-line ring-1 ring-line">
					<p className="flex items-center bg-surface px-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">
						{SYSTEMS}
					</p>
					<System name="X Ads API" load={smoke} pick={(d) => d.x} />
					<System name="BigQuery" load={smoke} pick={(d) => d.bq} />
					<System name="Database" load={smoke} pick={(d) => d.db} />
				</section>

				<div className="grid gap-2">
					{banners.map((b) => (
						<InfoBanner key={b.title} variant={b.kind} title={b.title}>
							{b.body}
							{b.link && (
								<>
									{" "}
									<a href={b.link.href} className="font-medium underline">
										{b.link.text}
									</a>
								</>
							)}
						</InfoBanner>
					))}

					{accounts.state === "failed" && (
						<InfoBanner variant="danger" title="Ad accounts could not be read.">
							{accounts.why}
						</InfoBanner>
					)}
				</div>

				<div className="grid min-h-0 gap-3 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
					{list.length > 0 && (
						<Panel title={ACCOUNTS_PANEL}>
							<ul className="grid list-none gap-3 p-0">
								{list.map((a) => {
									const status = approvalStatus(a.approvalStatus);
									return (
										<li key={a.id} id={accountCardId(a.xAccountId)}>
											<Card className="flex h-full flex-col gap-3 p-4">
												<div className="flex items-start justify-between gap-2">
													<div className="min-w-0">
														<h3 className="font-semibold text-content">{a.name}</h3>
														<p className="mt-0.5 font-mono text-xs text-muted">{a.xAccountId}</p>
													</div>
													<Badge variant={badgeVariant(status.tone)}>{status.label}</Badge>
												</div>
												<p className="text-xs text-muted">{syncedLabel(a.syncedAt)}</p>
												<ul className="list-none p-0 text-xs text-muted">
													{instrumentLines(
														readsInstruments(list.length)
															? (instruments[a.xAccountId] ?? { state: "loading" })
															: { state: "skipped", accounts: list.length },
													).map((l) => (
														<li key={l.key}>{l.text}</li>
													))}
												</ul>
												<ul className="list-none p-0 text-xs text-muted">
													{signalLines(
														readsInstruments(list.length)
															? (signals[a.xAccountId] ?? { state: "loading" })
															: { state: "skipped", accounts: list.length },
													).map((l) => (
														<li key={l.key}>{l.text}</li>
													))}
												</ul>
												{readsInstruments(list.length) && (
													<Button
														variant={a.xAccountId === shown ? "outline" : "ghost"}
														size="sm"
														disabled={a.xAccountId === shown}
														onClick={() => setChosen(a.xAccountId)}
													>
														{a.xAccountId === shown ? ANALYTICS_SHOWN : SHOW_ANALYTICS}
													</Button>
												)}
											</Card>
										</li>
									);
								})}
							</ul>
						</Panel>
					)}

					{list.length > 0 && readsInstruments(list.length) && (
						<section className="min-h-0 min-w-0 overflow-y-auto">
							{list
								.filter((a) => a.xAccountId === shown)
								.map((a) => {
									const held = instruments[a.xAccountId];
									return (
										<OverviewAnalytics
											key={a.id}
											account={a}
											state={overviewStatsState(stats[a.xAccountId] ?? { state: "loading" })}
											currency={held?.state === "ready" ? accountCurrency(held.instruments) : null}
											names={names[a.xAccountId] ?? { state: "loading" }}
											signals={signalLines(signals[a.xAccountId] ?? { state: "loading" })}
										/>
									);
								})}
						</section>
					)}
				</div>
			</div>
		</Page>
	);
}

const DOT: Record<string, string> = {
	ok: "bg-success",
	error: "bg-danger",
	skipped: "bg-muted",
	unknown: "bg-muted",
};

/**
 * A dependency line names what it actually reached — which database on which
 * instance, which dataset. "Connected" alone does not answer "connected to
 * what?", and on a shared instance that question has a real wrong answer.
 */
function System({
	name,
	load,
	pick,
}: {
	name: string;
	load: Load<Smoke>;
	pick: (d: Smoke) => Check | undefined;
}) {
	const check = load.state === "ready" ? pick(load.data) : undefined;
	const state = load.state === "failed" ? "error" : (check?.state ?? "unknown");
	const target = check?.target;
	const detail =
		load.state === "failed"
			? "Status check unavailable"
			: load.state === "loading"
				? "Checking…"
				: (check?.detail ?? "");

	return (
		<div className="flex min-w-[12rem] flex-1 items-start gap-2 bg-surface px-3 py-2">
			<span className={`mt-1.5 h-2 w-2 flex-none rounded-full ${DOT[state]}`} aria-hidden="true" />
			<div className="min-w-0">
				<p className="text-sm font-semibold text-content">{name}</p>
				{target && <p className="mt-0.5 break-words font-mono text-xs text-content">{target}</p>}
				<p className="mt-0.5 break-words text-xs text-muted">{detail}</p>
			</div>
		</div>
	);
}

export function badgeVariant(tone: string): "success" | "warning" | "error" | "default" {
	if (tone === "ok") return "success";
	if (tone === "warn") return "warning";
	if (tone === "err") return "error";
	return "default";
}

function roleLabel(role: string): string {
	return role === "super_admin"
		? "Super admin"
		: role === "admin"
			? "Admin"
			: role === "manager"
				? "Manager"
				: "User";
}
